import { Brain, Download, Shield, TrendingUp, Target, Clock, MapPin, AlertTriangle, Activity, User, Bot, ChevronDown, ChevronUp, Hammer, Zap, Globe, Fingerprint as FpIcon, Lock, Chrome, Info } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { useIntelStats, useAttackerProfiles, useDetectedTools, useThreatActors, useApi, useFingerprints } from '../lib/hooks';
import type { AttackerProfile, HoneypotSuggestion, AttackPatternAnalysis, PathIntelligence, Fingerprint } from '../lib/api';
import { useState, useEffect } from 'react';
import { getTechniqueName, getTechniqueDescription, getTechniqueCategory, getTechniqueCategoryColor } from '../lib/mitre-techniques';
import { SplitView } from '../components/SplitView';
import { ENGAGEMENT_VOCAB } from '../lib/engagement-vocab';

function formatTimeWasted(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60000)}min`;
}

function getSophisticationLabel(score: number): { label: string; level: number; color: string } {
  if (score >= 80) return { label: 'Expert', level: 5, color: 'text-red-400' };
  if (score >= 60) return { label: 'Professional', level: 4, color: 'text-orange-400' };
  if (score >= 40) return { label: 'Intermediate', level: 3, color: 'text-yellow-400' };
  if (score >= 20) return { label: 'Novice', level: 2, color: 'text-blue-400' };
  return { label: 'Script Kiddie', level: 1, color: 'text-gray-400' };
}

function getThreatColor(confidence: number): string {
  if (confidence >= 90) return 'text-red-400 bg-red-500/10 border-red-500/20';
  if (confidence >= 70) return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
  if (confidence >= 50) return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
  return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
}

function AttackerProfileCard({ profile }: { profile: AttackerProfile }) {
  const [expanded, setExpanded] = useState(false);
  const soph = getSophisticationLabel(profile.sophistication_score);
  const location = profile.source_countries[0] || 'Unknown';
  const tools = profile.tools_detected.length > 0 ? profile.tools_detected.slice(0, 3).join(', ') : 'Manual testing';
  const phases = profile.attack_phases.length > 0 ? profile.attack_phases.join(' → ') : 'reconnaissance';
  
  return (
    <div className={`bg-sg-card border rounded-xl overflow-hidden ${getThreatColor(profile.confidence)}`}>
      {/* Compact Header - Always Visible */}
      <div 
        className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {profile.automation_detected ? (
              <Bot className="w-5 h-5 text-purple-400" />
            ) : (
              <User className="w-5 h-5 text-blue-400" />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm">
                  {profile.automation_detected ? 'Automated Scanner' : 'Human Operator'}
                </h3>
                <span className="text-xs px-2 py-0.5 bg-white/10 rounded">
                  {profile.confidence}%
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {profile.session_count} sessions • {location}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-1 ${soph.color}`}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div 
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${
                    i < soph.level ? 'bg-current' : 'bg-gray-700'
                  }`}
                />
              ))}
            </div>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expandable Details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-white/10">
          <div className="grid grid-cols-2 gap-4 pt-4 mb-4">
            <div>
              <div className="flex items-center gap-1.5 text-sm opacity-75 mb-1">
                <MapPin className="w-4 h-4" />
                Location
              </div>
              <div className="font-medium">{location}</div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-sm opacity-75 mb-1">
                <Target className="w-4 h-4" />
                Tools Used
              </div>
              <div className="font-medium text-sm">{tools}</div>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="opacity-75">Attack Pattern:</span>
              <span className="font-medium capitalize">{phases}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="opacity-75">Sessions:</span>
              <span className="font-medium">{profile.session_count}</span>
            </div>
            {(profile.deceptions_engaged > 0 || profile.time_wasted_total_ms > 0) && (
              <div
                className="flex items-center justify-between text-sm"
                title={ENGAGEMENT_VOCAB.timeBurned.hint}
              >
                <span className="opacity-75">{ENGAGEMENT_VOCAB.timeBurned.label}:</span>
                <span className="font-medium">
                  {profile.deceptions_engaged > 0 ? `${profile.deceptions_engaged} · ` : ''}
                  {formatTimeWasted(profile.time_wasted_total_ms)} {ENGAGEMENT_VOCAB.timeWasted.short.toLowerCase()}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Shield className="w-3 h-3" />
              <span>Real attack data from live sessions</span>
            </div>
            <div className="text-xs">
              <span className="opacity-75">Sophistication: </span>
              <span className={`font-semibold ${soph.color}`}>{soph.label}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Adaptive Intelligence Components
function AdaptiveIntelligenceTab() {
  const api = useApi();
  const [suggestions, setSuggestions] = useState<HoneypotSuggestion[]>([]);
  const [patterns, setPatterns] = useState<AttackPatternAnalysis | null>(null);
  const [paths, setPaths] = useState<PathIntelligence[]>([]);
  const [loading, setLoading] = useState(true);
  const [implementing, setImplementing] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [suggestionsData, patternsData, pathsData] = await Promise.all([
        api.getHoneypotSuggestions(10),
        api.getAttackPatternAnalysis(),
        api.getPathIntelligence(10, 20),
      ]);
      setSuggestions(suggestionsData.suggestions);
      setPatterns(patternsData);
      setPaths(pathsData.paths);
    } catch (error) {
      console.error('Failed to load adaptive intelligence:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImplement = async (path: string) => {
    setImplementing(path);
    try {
      await api.implementSuggestion(path);
      await loadData();
    } catch (error) {
      console.error('Failed to implement suggestion:', error);
    } finally {
      setImplementing(null);
    }
  };

  const categoryColors: Record<string, string> = {
    git_recon: 'bg-purple-500',
    secret_scanning: 'bg-red-500',
    api_discovery: 'bg-blue-500',
    path_traversal: 'bg-yellow-500',
    backup_hunting: 'bg-orange-500',
    config_mining: 'bg-pink-500',
    cloud_metadata: 'bg-indigo-500',
    container_escape: 'bg-cyan-500',
    iac_discovery: 'bg-teal-500',
    ci_cd_secrets: 'bg-green-500',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading adaptive intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      {patterns && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Suggestions</div>
            <div className="text-3xl font-bold text-white">{suggestions.length}</div>
            <div className="text-xs text-gray-500 mt-1">Ready to implement</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Coverage Gaps</div>
            <div className="text-3xl font-bold text-orange-500">{patterns.insights.coverage_gaps}</div>
            <div className="text-xs text-gray-500 mt-1">Undefended patterns</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Patterns Learned</div>
            <div className="text-3xl font-bold text-blue-500">{patterns.insights.total_patterns}</div>
            <div className="text-xs text-gray-500 mt-1">Attack patterns tracked</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Top Category</div>
            <div className="text-lg font-bold text-white capitalize">{patterns.insights.most_targeted_category.replace(/_/g, ' ')}</div>
            <div className="text-xs text-gray-500 mt-1">Most targeted</div>
          </div>
        </div>
      )}

      {/* Honeypot Suggestions */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg">
        <div className="p-6 border-b border-gray-800">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Recommended Honeypots
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            AI-recommended deceptions based on trending attack patterns
          </p>
        </div>
        <div className="p-6">
          {suggestions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No new honeypots suggested yet</p>
              <p className="text-sm text-gray-600 mt-1">System is learning from traffic patterns...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.suggested_path}
                  className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <code className="text-blue-400 font-mono text-sm bg-gray-900 px-2 py-1 rounded">
                          {suggestion.suggested_path}
                        </code>
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-medium rounded">
                          {suggestion.suggested_variant}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 mb-2">
                        <div>
                          <div className="text-xs text-gray-500">Confidence</div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500"
                                style={{ width: `${suggestion.confidence_score}%` }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-white">{suggestion.confidence_score}%</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Requests</div>
                          <div className="text-sm font-semibold text-white mt-1">{suggestion.request_frequency}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Attackers</div>
                          <div className="text-sm font-semibold text-orange-400 mt-1">{suggestion.unique_attackers}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Effectiveness</div>
                          <div className="text-sm font-semibold text-green-400 mt-1">{suggestion.estimated_effectiveness}%</div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        <span className="text-gray-400">Reason:</span> {suggestion.reason}
                      </div>
                    </div>
                    <button
                      onClick={() => handleImplement(suggestion.suggested_path)}
                      disabled={implementing === suggestion.suggested_path}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {implementing === suggestion.suggested_path ? 'Implementing...' : 'Implement'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Fingerprints Tab Component
function FingerprintsTab() {
  const { data } = useFingerprints();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  const fingerprints = data?.fingerprints ?? [];
  
  // Sort by last seen (most recent first)
  const sortedFingerprints = [...fingerprints].sort((a, b) => 
    new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime()
  );
  
  // Calculate stats (24h timeframe to match Active Attackers metric)
  const activeFPs = sortedFingerprints.filter(fp => {
    const hoursSince = Math.floor((Date.now() - new Date(fp.lastSeen).getTime()) / (1000 * 60 * 60));
    return hoursSince < 24;
  }).length;
  
  const totalSessions = sortedFingerprints.reduce((sum, fp) => sum + fp.sessionCount, 0);
  const uniqueTools = new Set(sortedFingerprints.map(fp => fp.browserFamily).filter(Boolean)).size;

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-400 mb-1">What are Fingerprints?</h3>
            <p className="text-sm text-blue-300/80">
              Fingerprints are unique identifiers created from SSL/TLS and HTTP characteristics. They help track attackers even when they change IP addresses or use VPNs. JA3 fingerprints TLS handshakes, while JA4 analyzes HTTP/2 patterns.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Total Fingerprints</div>
          <div className="text-3xl font-bold text-white">{sortedFingerprints.length}</div>
          <div className="text-xs text-gray-500 mt-1">Unique clients tracked</div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Active (24h)</div>
          <div className="text-3xl font-bold text-green-500">{activeFPs}</div>
          <div className="text-xs text-gray-500 mt-1">Last 24 hours</div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Total Sessions</div>
          <div className="text-3xl font-bold text-blue-500">{totalSessions}</div>
          <div className="text-xs text-gray-500 mt-1">Across all fingerprints</div>
        </div>
      </div>

      {/* Fingerprints List */}
      {sortedFingerprints.length === 0 ? (
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <FpIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No fingerprints collected yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedFingerprints.map((fp) => {
            const hoursSinceLastSeen = Math.floor((Date.now() - new Date(fp.lastSeen).getTime()) / (1000 * 60 * 60));
            const daysSinceLastSeen = Math.floor(hoursSinceLastSeen / 24);
            const isActive = hoursSinceLastSeen < 24; // Active badge if seen in last 24h
            const isHighVolume = fp.sessionCount > 10;
            const expanded = expandedId === fp.id;
            
            const threatColor = isActive && isHighVolume 
              ? 'border-red-500/50 bg-red-500/5'
              : isActive 
              ? 'border-orange-500/50 bg-orange-500/5'
              : 'border-gray-700 bg-gray-900';

            return (
              <div key={fp.id} className={`border rounded-lg overflow-hidden ${threatColor}`}>
                {/* Compact Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => setExpandedId(expanded ? null : fp.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <FpIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-white">
                              {fp.browserFamily || 'Unknown Client'}
                            </span>
                            {isActive && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                Active
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {fp.osFamily || 'Unknown OS'} • {fp.sessionCount} sessions
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Last seen {daysSinceLastSeen === 0 ? 'today' : `${daysSinceLastSeen}d ago`}
                        </div>
                      </div>
                    </div>
                    
                    <button className="text-xs text-blue-400 hover:text-blue-300">
                      {expanded ? 'Hide' : 'Details'}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expanded && (
                  <div className="border-t border-gray-800 p-4 space-y-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-2 font-medium">FINGERPRINT ANALYSIS</div>
                      <p className="text-sm text-gray-400 leading-relaxed">
                        This unique fingerprint identifies a specific tool or browser configuration.
                        {fp.sessionCount > 5 
                          ? ` It has been seen ${fp.sessionCount} times, suggesting ${fp.sessionCount > 20 ? 'persistent scanning activity' : 'repeated reconnaissance'}.`
                          : ' This is a relatively new or infrequent visitor.'}
                        {isActive && ' This fingerprint is currently active.'}
                      </p>
                    </div>

                    {/* Technical Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Lock className="w-4 h-4 text-purple-400" />
                          <span className="text-xs text-gray-500 font-medium">JA3 HASH (TLS)</span>
                        </div>
                        <code className="text-xs text-purple-300 bg-gray-900 px-2 py-1 rounded block overflow-x-auto">
                          {fp.ja3Hash || 'Not available'}
                        </code>
                        <p className="text-xs text-gray-600 mt-1">Identifies SSL/TLS client behavior</p>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Chrome className="w-4 h-4 text-blue-400" />
                          <span className="text-xs text-gray-500 font-medium">JA4 HASH (HTTP/2)</span>
                        </div>
                        <code className="text-xs text-blue-300 bg-gray-900 px-2 py-1 rounded block overflow-x-auto">
                          {fp.ja4Hash || 'Not available'}
                        </code>
                        <p className="text-xs text-gray-600 mt-1">Newer HTTP/2 fingerprinting method</p>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-800 text-xs text-gray-500">
                      <div>
                        <span className="text-gray-600">First seen:</span> {new Date(fp.firstSeen).toLocaleString()}
                      </div>
                      <div>
                        <span className="text-gray-600">Last seen:</span> {new Date(fp.lastSeen).toLocaleString()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Intelligence() {
  const { isConnected, allTenants } = useAuth();
  const { data: stats } = useIntelStats();
  const { data: profilesData, isLoading: profilesLoading } = useAttackerProfiles();
  const { data: toolsData } = useDetectedTools();
  const { data: actorsData } = useThreatActors();
  const [activeTab, setActiveTab] = useState<'actors' | 'fingerprints' | 'adaptive'>('actors');

  const profiles = profilesData?.profiles ?? [];
  const tools = toolsData?.tools ?? [];
  const actors = actorsData?.threat_actors ?? [];

  if (!isConnected) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold text-white mb-2">Intelligence</h2>
        <p className="text-gray-400 mb-8">Threat actor profiles and adaptive honeypot recommendations</p>
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <Brain className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Connect to the control plane in Settings</p>
        </div>
      </div>
    );
  }

  const content = (tenantId?: string) => (
    <>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="w-7 h-7 text-purple-400" />
          <h2 className="text-2xl font-bold text-white">Intelligence</h2>
        </div>
        <p className="text-gray-400">Threat actor profiles and adaptive honeypot recommendations</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="bg-sg-card border border-sg-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-5 h-5 text-sg-accent" />
            <div className="text-2xl font-bold text-white">{stats?.active_profiles ?? 0}</div>
          </div>
          <div className="text-sm text-gray-400">Active Attackers</div>
        </div>
        <div className="bg-sg-card border border-sg-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <Target className="w-5 h-5 text-blue-400" />
            <div className="text-2xl font-bold text-white">{stats?.unique_tools ?? 0}</div>
          </div>
          <div className="text-sm text-gray-400">Tools Detected</div>
        </div>
        <div className="bg-sg-card border border-sg-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            <div className="text-2xl font-bold text-white">{stats?.campaigns ?? 0}</div>
          </div>
          <div className="text-sm text-gray-400">Campaigns</div>
        </div>
        <div className="bg-sg-card border border-sg-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <div className="text-2xl font-bold text-white">{stats?.high_confidence_iocs ?? 0}</div>
          </div>
          <div className="text-sm text-gray-400">Threat Indicators</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-gray-800">
        <button
          onClick={() => setActiveTab('actors')}
          className={`px-6 py-3 font-medium transition-colors relative ${
            activeTab === 'actors'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Threat Actors
          </div>
        </button>
        <button
          onClick={() => setActiveTab('fingerprints')}
          className={`px-6 py-3 font-medium transition-colors relative ${
            activeTab === 'fingerprints'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <FpIcon className="w-4 h-4" />
            Fingerprints
          </div>
        </button>
        <button
          onClick={() => setActiveTab('adaptive')}
          className={`px-6 py-3 font-medium transition-colors relative ${
            activeTab === 'adaptive'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Adaptive Intelligence
          </div>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'actors' ? (
        <>
          {/* Active Threat Actors */}
          {profiles.length === 0 ? (
            <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
              <Activity className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Building Intelligence Profile...</h3>
              <p className="text-gray-500 mb-2">
                {profilesLoading ? 'Analyzing attacker behavior...' : 'As attackers interact with your system, detailed profiles will appear here.'}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-red-400" />
                  <h3 className="text-xl font-semibold text-white">Active Threat Actors</h3>
                </div>
                <p className="text-gray-400 text-sm">Live attacker profiles based on real session data — Click to expand</p>
              </div>
              <div className="space-y-3 mb-8">
                {profiles.map((profile) => (
                  <AttackerProfileCard key={profile.id} profile={profile} />
                ))}
              </div>
            </>
          )}

          {/* Named Threat Actors */}
          {actors.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-red-400" />
                <h3 className="text-xl font-semibold text-white">Named Threat Actors</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4">Manually attributed threat actors with confirmed activity</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {actors.map((actor) => (
                  <div 
                    key={actor.id} 
                    className={`bg-sg-card border rounded-xl p-5 ${getThreatColor(actor.confidence)}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-1">{actor.name}</h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          <span>Active since {new Date(actor.first_seen).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        actor.confidence >= 90 ? 'bg-red-500/20 text-red-400' :
                        actor.confidence >= 70 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {actor.confidence}% confidence
                      </span>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-3 gap-3 mb-3 pb-3 border-b border-white/10">
                      <div>
                        <div className="text-xs text-gray-500">Sessions</div>
                        <div className="text-sm font-semibold text-white">{actor.session_count}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Fingerprints</div>
                        <div className="text-sm font-semibold text-white">{actor.fingerprint_ids.length}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Last Seen</div>
                        <div className="text-sm font-semibold text-white">
                          {Math.floor((Date.now() - new Date(actor.last_seen).getTime()) / (1000 * 60 * 60 * 24))}d ago
                        </div>
                      </div>
                    </div>

                    {/* Techniques */}
                    {actor.techniques.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs text-gray-500 mb-2">MITRE ATT&CK Techniques</div>
                        <div className="flex flex-wrap gap-1">
                          {actor.techniques.slice(0, 5).map((tech) => {
                            const category = getTechniqueCategory(tech);
                            const color = getTechniqueCategoryColor(category);
                            return (
                              <span 
                                key={tech} 
                                className={`px-2 py-0.5 rounded text-xs font-mono ${color}`}
                                title={getTechniqueName(tech)}
                              >
                                {tech}
                              </span>
                            );
                          })}
                          {actor.techniques.length > 5 && (
                            <span className="text-xs text-gray-500">+{actor.techniques.length - 5} more</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Infrastructure */}
                    {actor.infrastructure && Object.keys(actor.infrastructure).length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs text-gray-500 mb-2">Infrastructure</div>
                        <div className="text-xs text-gray-300 bg-sg-dark/50 p-2 rounded font-mono">
                          {Object.entries(actor.infrastructure).slice(0, 3).map(([key, value]) => (
                            <div key={key} className="truncate">
                              <span className="text-gray-500">{key}:</span> {JSON.stringify(value)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {actor.notes && (
                      <div className="text-xs text-gray-400 italic border-t border-white/10 pt-3">
                        {actor.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tools & Techniques */}
          {tools.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Hammer className="w-5 h-5 text-blue-400" />
                <h3 className="text-xl font-semibold text-white">Tools & Techniques</h3>
              </div>
              <div className="bg-sg-card border border-sg-border rounded-xl p-6">
                <div className="space-y-3">
                  {tools.slice(0, 8).map((tool) => {
                    const percentage = toolsData ? (tool.count / toolsData.total_detections) * 100 : 0;
                    return (
                      <div key={tool.tool}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-300 capitalize">{tool.tool.replace(/-/g, ' ')}</span>
                          <span className="text-gray-400">{tool.count} ({Math.round(percentage)}%)</span>
                        </div>
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-sg-accent rounded-full transition-all" 
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      ) : activeTab === 'fingerprints' ? (
        <FingerprintsTab />
      ) : (
        <AdaptiveIntelligenceTab />
      )}
    </>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {allTenants.length > 1 ? (
        <SplitView>{content}</SplitView>
      ) : (
        content()
      )}
    </div>
  );
}
